import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Dialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddProveedorMutation } from "@/features/auxiliar/proveedores/api/add-proveedor";
import { DeleteProveedorMutation } from "@/features/auxiliar/proveedores/api/delete-proveedor";
import { EditProveedorMutation } from "@/features/auxiliar/proveedores/api/edit-proveedor";
import { getAllProveedores, useGetAllProveedoresQuery } from "@/features/auxiliar/proveedores/api/get-all-proveedores";
import { GetProveedorQuery } from "@/features/auxiliar/proveedores/api/get-proveedor";
import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { useFilters } from "@/components/filters";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { GetCuentaCorrienteQuery } from "@/features/auxiliar/cuenta-corrientes/api/get-cuenta-corriente";
import ViewCuentaCorriente from "@/features/auxiliar/cuenta-corrientes/components/view-cuenta-corriente";
import { SchemaFormHook } from "@/components/form";
import AddProveedorForm, { addProveedorFormSchema } from "@/features/auxiliar/proveedores/components/add-proveedor-form";
import EditProveedorForm, { editProveedorFormSchema } from "@/features/auxiliar/proveedores/components/edit-proveedor-form";

export default function ProveedoresPage() {
    const openModal = useOpenModal();

    const hasWritePermiso = useHasPermisos(Permiso.WriteProveedores);
    const hasReadCuentaCorrientePermiso = useHasPermisos(Permiso.ReadCuentaCorrientes);

    const filterData = useFilters({
        filters: [
            { type: "string", label: "Nombre", name: "nombre" },
            { type: "string", label: "N° Documento", name: "numDocumento" },
            { type: "string", label: "Email", name: "email" },
            { type: "string", label: "Teléfono", name: "telefono" },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllProveedoresQuery(filterData.filterData);

    const columnData = useColumns({
        columns: [
            { key: "nombre", header: "Nombre", render: row => formatString(row.nombre) },
            { key: "numDocumento", header: "N° Documento", render: row => formatString(row.numDocumento) },
            { key: "telefono", header: "Teléfono", render: row => formatString(row.telefono) },
            { key: "email", header: "Email", render: row => formatString(row.email) },
            { key: "direccion", header: "Dirección", render: row => formatString(row.direccion), show: false },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ],
        key: "proveedores"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Proveedores">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    hasWritePermiso && {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            {hasReadCuentaCorrientePermiso && <DataTable.EntryOption.Button Icon={AccountBalanceIcon} title="Cuenta Corriente" onClick={() => openModal("viewCuentaCorriente", row.personaId)} />}
                            <DataTable.EntryOption.EditButton onClick={() => openModal("edit", row.id)} />
                            <DataTable.EntryOption.DeleteButton disabled={row.protegido} onClick={() => openModal("delete", row)} />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Drawer code="add">
            <ModalHeader>Agregar Proveedor</ModalHeader>
            <AddProveedorMutation>
                {mutation => <SchemaFormHook
                    schema={addProveedorFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify("El proveedor se ha agregado correctamente.");
                    }}
                    submitOnce
                    closeModalOnSubmit
                >
                    {form => <AddProveedorForm form={form} margin={3} />}
                </SchemaFormHook>}
            </AddProveedorMutation>
        </Drawer>

        <Drawer code="edit">
            <ModalHeader>Editar Proveedor</ModalHeader>
            <ModalValueExtractor>
                {proveedorId => <GetProveedorQuery id={proveedorId}>
                    {query => <DataFetcher flex={1} query={query}>
                        {proveedor => <EditProveedorMutation id={proveedor.id}>
                            {mutation => <SchemaFormHook
                                schema={editProveedorFormSchema}
                                onSubmit={async ({ value }) => {
                                    await mutation.mutateAsync(value);
                                    notify("El proveedor se ha editado correctamente.");
                                }}
                                defaultValues={{
                                    ...proveedor
                                }}
                                submitOnce
                                closeModalOnSubmit
                            >
                                {form => <EditProveedorForm form={form} margin={3} />}
                            </SchemaFormHook>}
                        </EditProveedorMutation>}
                    </DataFetcher>}
                </GetProveedorQuery>}
            </ModalValueExtractor>
        </Drawer>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {proveedor => <DeleteProveedorMutation id={proveedor.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Borrar Proveedor "${proveedor.nombre}"`}
                        description={`¿Estás seguro que quieres borrar el proveedor "${proveedor.nombre}"?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`El proveedor "${proveedor.nombre}" se ha borrado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteProveedorMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <Dialog code="viewCuentaCorriente" size="lg" fullHeight>
            <ModalValueExtractor>
                {personaId => <GetCuentaCorrienteQuery personaId={personaId}>
                    {query => <DataFetcher query={query}>
                        {cuenta => <>
                            <ModalHeader>Cuenta Corriente - {cuenta.personaNombre}</ModalHeader>
                            <ViewCuentaCorriente
                                cuenta={cuenta}
                                flex={1}
                                padding={3}
                            />
                        </>}
                    </DataFetcher>}
                </GetCuentaCorrienteQuery>}
            </ModalValueExtractor>
        </Dialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllProveedores}
            defaultFileTitle="Proveedores"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllProveedores}
            defaultFileTitle="Proveedores"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllProveedores}
            defaultFileTitle="Proveedores"
        />
    </ListPage>
}